import DevError from '/shared/errors/DevError';
import { urlApplicationRoot } from '/business/shared/util/Statics';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import type { DecoratedQuestionTreeItem, QuestionCollection, QuestionTreeItem } from '/business/shared/functions/types';

type SectionPropSettings = {
  type: string;
  subPropName: 'groups' | 'questions' | 'subQuestions';
  depth: number;
};

export default function decorateQuestions(
  questionsData: QuestionCollection | null | undefined = null,
): DecoratedQuestionTreeItem[] {
  if (!questionsData || !Array.isArray(questionsData)) {
    throw new DevError('Bad data sent to decorateQuestions');
  }
  const path: string[] = [];

  path.push(`/${urlApplicationRoot}`);
  path.push('questions');

  /*
  Define the top levels, any depths that are NOT found in this set of properties, will be classified as a subQuestion
   */
  const sectionPropOverrideDictionary: SectionPropSettings[] = [
    {
      type: 'section',
      subPropName: 'groups',
      depth: 0,
    },
    {
      type: 'group',
      subPropName: 'questions',
      depth: 1,
    },
    {
      type: 'question',
      subPropName: 'subQuestions',
      depth: 2,
    },
  ];

  const getItemSettings = (depth: number): SectionPropSettings => {
    let overrideSectionProps = sectionPropOverrideDictionary.find((sectionProp) => sectionProp.depth === depth);
    if (!overrideSectionProps) {
      overrideSectionProps = {
        type: 'subQuestion',
        subPropName: 'subQuestions',
        depth,
      };
    }
    return {
      // set default to subQuestion, which means we have an infinite depth in terminology
      // depth is set to what is passed in,
      // and if the depth has any overrides, they will be spread in
      depth,
      subPropName: overrideSectionProps.subPropName,
      type: overrideSectionProps.type,
    };
  };
  const recur = (data: QuestionTreeItem, parentPath: string[] = [], depth = 0): QuestionTreeItem => {
    const sectionPropSettings = getItemSettings(depth);
    let combinePath = parentPath.concat(data.id ?? '');

    if (data?.selector?.injectQuestionBranchSelector && !parentPath.includes(QUESTION_BRANCH_ACTIVITY)) {
      /*
      inject 'activity' into all questions, as the question system schema's
      applies the activity data bespoke layouts
      Within getQuestionsBreadcrumbs we tear out the url slug
       */
      combinePath = parentPath.concat(data.id ?? '').concat(QUESTION_BRANCH_ACTIVITY);
    }

    let subItems = data[sectionPropSettings.subPropName] || [];
    if (subItems && subItems.length > 0) {
      subItems = subItems.map((subItem) => {
        return recur(subItem, combinePath, depth + 1);
      });
    }

    return {
      ...data,
      depth,
      type: sectionPropSettings.type,
      path: [...combinePath],
      [sectionPropSettings.subPropName]: subItems,
    };
  };
  const initialPass = questionsData.map((section) => {
    if (!section) {
      return recur({}, path, 0);
    }
    return recur(section, path, 0);
  });

  // This is a rewrite from two original hardcoded mappings,
  //  it was easier to conceptually just handle in two different
  //  recursions that emulated old behaviour
  const secondaryRecursion = (
    entry: QuestionTreeItem,
    parent: QuestionTreeItem | null,
    depth: number,
  ): QuestionTreeItem => {
    const sectionPropSettings = getItemSettings(depth);
    let subItems = entry[sectionPropSettings.subPropName];

    const newParent = {
      ...entry,
      parent,
      children: subItems,
      // we declare children here, so 'parent' will have recursive access to children
      //  when passed in to secondaryRecursion
      numOfChildren: subItems?.length || 0,
      [sectionPropSettings.subPropName]: subItems,
    };

    const fullChildren: QuestionTreeItem[] = [];
    if (subItems && subItems.length > 0) {
      // reprocess all sub-items
      subItems = subItems.map((subItem) => {
        return secondaryRecursion(subItem, newParent, depth + 1);
      });
      subItems.map((subItem) => {
        fullChildren.push(subItem);
        if (depth > 1) {
          fullChildren.push(...(subItem.children || []));
        }
        return null;
      });
    }

    return {
      ...newParent,
      // now re-set children props now that we have re-processed them
      children: fullChildren,
      [sectionPropSettings.subPropName]: fullChildren,
    };
  };

  return initialPass.map((item) => {
    return secondaryRecursion(item, null, 0) as DecoratedQuestionTreeItem;
  });
}
